import { getMascotsByIds, getMascotById, type Mascot } from '@/constants/mascots'

export const useMascots = () => {
  /**
   * Get mascots by their IDs
   */
  const getMascots = (ids: string[]): Mascot[] => {
    return getMascotsByIds(ids)
  }

  /**
   * Get a single mascot by ID
   */
  const getMascot = (id: string): Mascot | undefined => {
    return getMascotById(id)
  }

  /**
   * Check if a mascot is an adult
   */
  const isAdultMascot = (id: string): boolean => {
    const mascot = getMascot(id)
    return mascot?.isAdult || false
  }

  /**
   * Get all adult mascots from a list of IDs
   */
  const getAdultMascots = (ids: string[]): Mascot[] => {
    return getMascots(ids).filter((mascot) => mascot.isAdult)
  }

  /**
   * Get all child mascots from a list of IDs
   */
  const getChildMascots = (ids: string[]): Mascot[] => {
    return getMascots(ids).filter((mascot) => !mascot.isAdult)
  }

  /**
   * Validate mascot IDs (check if they exist)
   */
  const validateMascotIds = (ids: string[]): { valid: string[]; invalid: string[] } => {
    const valid: string[] = []
    const invalid: string[] = []

    ids.forEach((id) => {
      if (getMascot(id)) {
        valid.push(id)
      } else {
        invalid.push(id)
      }
    })

    return { valid, invalid }
  }

  /**
   * Get mascot display configuration based on content type
   */
  const getMascotConfig = (contentType: string) => {
    const configs = {
      textBlock: {
        layout: 'horizontal' as const,
        size: 'medium' as const,
        showHeightDifference: true,
      },
      article: {
        layout: 'horizontal' as const,
        size: 'large' as const,
        showHeightDifference: true,
      },
      cta: {
        layout: 'horizontal' as const,
        size: 'medium' as const,
        showHeightDifference: true,
      },
      news: {
        layout: 'horizontal' as const,
        size: 'small' as const,
        showHeightDifference: true,
      },
      category: {
        layout: 'grid' as const,
        size: 'medium' as const,
        showHeightDifference: false,
      },
      portal: {
        layout: 'grid' as const,
        size: 'medium' as const,
        showHeightDifference: true,
      },
      newsBlock: {
        layout: 'horizontal' as const,
        size: 'medium' as const,
        showHeightDifference: true,
      },
      jokeBlock: {
        layout: 'horizontal' as const,
        size: 'medium' as const,
        showHeightDifference: true,
      },
      factsBlock: {
        layout: 'horizontal' as const,
        size: 'medium' as const,
        showHeightDifference: true,
      },
    }

    return configs[contentType as keyof typeof configs] || configs.textBlock
  }

  return {
    getMascots,
    getMascot,
    isAdultMascot,
    getAdultMascots,
    getChildMascots,
    validateMascotIds,
    getMascotConfig,
  }
}
